import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { getMqttClient, TOPICS, publishCmd, publishCamCmd, publishPzemCmd, reconnectMqtt as mqttReconnect } from "../lib/mqtt";
import { DEFAULT_SETTINGS } from "../lib/constants";
import { toast } from "sonner";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const loadLS = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
  catch { return fallback; }
};

const MAX_HISTORY = 360; // ~12 minutes of 2s readings

export function AppProvider({ children }) {
  const [theme, setTheme]               = useState(() => loadLS("cem_theme", "dark"));
  const [telemetry, setTelemetry]       = useState(null);
  const [history, setHistory]           = useState([]);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const [settings, setSettings]         = useState(() => {
    const saved = loadLS("cem_settings", {});
    const merged = { ...DEFAULT_SETTINGS, ...saved };
    // Don't let a previously-saved empty value wipe out a newly-seeded credential
    if (!merged.mqttPass)   merged.mqttPass   = DEFAULT_SETTINGS.mqttPass;
    if (!merged.mqttUser)   merged.mqttUser   = DEFAULT_SETTINGS.mqttUser;
    if (!merged.mqttHost)   merged.mqttHost   = DEFAULT_SETTINGS.mqttHost;
    if (!merged.grafanaUrl || merged.grafanaUrl.includes("cput-energy.grafana.net")) merged.grafanaUrl = DEFAULT_SETTINGS.grafanaUrl;
    return merged;
  });
  const [snapshots, setSnapshots]       = useState(() => loadLS("cem_gallery", []));
  const [camImage, setCamImage]         = useState(null);
  const [camState, setCamState]         = useState(null);
  const [lastMotion, setLastMotion]     = useState(null);
  const [mqttRevision, setMqttRevision] = useState(0);
  const [pzemOnline, setPzemOnline]     = useState(false);
  const clientRef = useRef(null);
  const settingsRef = useRef(settings);
  const alertCooldown = useRef({});
  const staleTimerRef = useRef(null);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  // persist theme
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark"); else root.classList.remove("dark");
    localStorage.setItem("cem_theme", JSON.stringify(theme));
  }, [theme]);

  useEffect(() => { localStorage.setItem("cem_settings", JSON.stringify(settings)); }, [settings]);
  useEffect(() => { localStorage.setItem("cem_gallery", JSON.stringify(snapshots)); }, [snapshots]);

  // MQTT connection — re-runs when mqttRevision changes (triggered by reconnect)
  useEffect(() => {
    console.log("[AppContext] MQTT useEffect running (revision:", mqttRevision, ")");
    const client = getMqttClient();
    clientRef.current = client;
    console.log("[AppContext] client.connected:", client.connected, "| client.reconnecting:", client.reconnecting);

    const onConnect = () => {
      console.log("[AppContext] onConnect fired — subscribing to topics");
      setMqttConnected(true);
      client.subscribe(TOPICS.PZEM,       { qos: 0 }, (err) => {
        if (err) console.error("[AppContext] subscribe PZEM error:", err);
        else console.log("[AppContext] subscribed →", TOPICS.PZEM);
      });
      client.subscribe(TOPICS.CAM_IMG,    { qos: 0 }, (err) => {
        if (err) console.error("[AppContext] subscribe CAM_IMG error:", err);
        else console.log("[AppContext] subscribed →", TOPICS.CAM_IMG);
      });
      client.subscribe(TOPICS.CAM_STATE,  { qos: 0 }, (err) => {
        if (err) console.error("[AppContext] subscribe CAM_STATE error:", err);
        else console.log("[AppContext] subscribed →", TOPICS.CAM_STATE);
      });
      client.subscribe(TOPICS.CAM_MOTION, { qos: 0 }, (err) => {
        if (err) console.error("[AppContext] subscribe CAM_MOTION error:", err);
        else console.log("[AppContext] subscribed →", TOPICS.CAM_MOTION);
      });
    };

    const onMessage = (topic, payload) => {
      const msg = payload.toString();
      console.log("[AppContext] onMessage — topic:", topic, "| raw:", msg.slice(0, 120));
      if (topic === TOPICS.PZEM) {
        try {
          const d = JSON.parse(msg);
          console.log("[AppContext] PZEM parsed OK —", {
            l1_ok: d.l1?.ok, l2_ok: d.l2?.ok, l3_ok: d.l3?.ok,
            dht_ok: d.dht_ok, dht_temp: d.dht_temp, dht_hum: d.dht_hum,
            gas_quality: d.gas_quality, gas_ppm: d.gas_ppm,
            fan_pct: d.fan_pct, fan_auto: d.fan_auto,
            pir: d.pir, light_pct: d.light_pct, ip: d.ip, uptime: d.uptime,
          });
          const phaseObj = (p) => p ? { voltage: p.v, current: p.a, power: p.w, energy: p.e, hz: p.hz, pf: p.pf, ok: p.ok } : null;
          const okPhases = [d.l1, d.l2, d.l3].filter((p) => p && p.ok);
          const avgPf = okPhases.length ? okPhases.reduce((s, p) => s + p.pf, 0) / okPhases.length : 0;
          const transformed = {
            // PZEM-004T phase readings (authoritative source)
            phases:        { L1: phaseObj(d.l1), L2: phaseObj(d.l2), L3: phaseObj(d.l3) },
            total_power:   (d.l1?.w ?? 0) + (d.l2?.w ?? 0) + (d.l3?.w ?? 0),
            power_factor:  avgPf,
            // DHT11 — prefixed to avoid conflict with DS18B20 on main ESP32
            dht_temp:      d.dht_ok ? d.dht_temp : null,
            dht_hum:       d.dht_ok ? d.dht_hum  : null,
            dht_ok:        !!d.dht_ok,
            // PZEM board fan — prefixed to avoid conflict with main ESP32 fan
            pzem_fan_pct:  d.fan_pct ?? 0,
            pzem_fan_auto: !!d.fan_auto,
            // PZEM board HC-SR501 PIR — prefixed to avoid conflict with main ESP32 AM312
            pzem_pir:      !!d.pir,
            // Light hub (unique to PZEM board)
            light_pct:     d.light_pct ?? 0,
            light_duty:    d.light_duty ?? 0,
            // MQ-135 gas sensor on PZEM board — prefixed to avoid conflict with main ESP32 MQ-135
            pzem_gas_raw:      d.gas_raw       ?? 0,
            pzem_gas_ppm:      d.gas_ppm       ?? 0,
            pzem_gas_quality:  d.gas_quality   ?? null,
            pzem_gas_thresh:   d.gas_thresh    ?? 600,
            pzem_fan_on_gas:   !!d.fan_on_gas,
            pzem_buzzer_on_gas:!!d.buzzer_on_gas,
            pzem_buzzer_on_pir:!!d.buzzer_on_pir,
            pzem_lcd_on:       d.lcd_on !== false,
            pzem_pir_on:       d.pir_on !== false,
            pzem_ip:       d.ip,
            pzem_uptime:   d.uptime,
          };
          setTelemetry((prev) => ({ ...(prev || {}), ...transformed }));
          setLastUpdate(new Date());
          // Mark board as online; reset 15-second staleness timer
          setPzemOnline(true);
          if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
          staleTimerRef.current = setTimeout(() => setPzemOnline(false), 15000);
          setHistory((h) => {
            const point = { ...transformed, _ts: Date.now() };
            return [...h.slice(-MAX_HISTORY), point];
          });
          // ── Alert checking (5-minute cooldown per type) ──
          const s = settingsRef.current;
          const now = Date.now();
          const cd = alertCooldown.current;
          const canAlert = (key) => !cd[key] || now - cd[key] > 300000;
          if (s.alertTemp && d.dht_ok && d.dht_temp >= (s.fanCritical ?? 65) && canAlert("temp")) {
            cd.temp = now;
            toast.error(`Temperature critical: ${d.dht_temp}°C`, { description: "DHT11 above critical threshold" });
          }
          if (s.alertPower && transformed.total_power >= 4500 && canAlert("power")) {
            cd.power = now;
            toast.warning(`High power: ${Math.round(transformed.total_power)} W`, { description: "Total load exceeds 4.5 kW" });
          }
          if (s.alertGas && d.gas_quality === "DANGER" && canAlert("gas")) {
            cd.gas = now;
            toast.error(`Air quality DANGER! ${d.gas_ppm != null ? Math.round(d.gas_ppm) + " ppm" : ""}`, { description: "MQ-135 — ventilate area immediately" });
          }
        } catch (e) {
          console.error("[AppContext] PZEM JSON parse error:", e?.message, "| raw:", msg.slice(0, 200));
        }
      } else if (topic === TOPICS.CAM_IMG) {
        console.log("[AppContext] camera image received, length:", msg.length);
        setCamImage(`data:image/jpeg;base64,${msg}`);
      } else if (topic === TOPICS.CAM_STATE) {
        try {
          const s = JSON.parse(msg);
          console.log("[AppContext] camera state:", s);
          setCamState(s);
        } catch (e) { console.error("[AppContext] CAM_STATE parse error:", e?.message); }
      } else if (topic === TOPICS.CAM_MOTION) {
        try {
          const m = JSON.parse(msg);
          console.log("[AppContext] camera motion:", m);
          if (m.motion) {
            setLastMotion(new Date());
            toast.warning("Motion detected!", { description: "Camera captured movement" });
          }
        } catch (e) { console.error("[AppContext] CAM_MOTION parse error:", e?.message); }
      }
    };

    const onDisconnect = () => {
      console.warn("[AppContext] MQTT disconnected");
      setMqttConnected(false);
      if (settingsRef.current.alertMqtt) toast.warning("MQTT disconnected", { description: "Reconnecting in 5 s…" });
    };
    const onError = (err) => {
      const reason = err?.message || String(err || "Unknown error");
      console.error("[AppContext] MQTT error:", reason, err);
      setMqttConnected(false);
      if (settingsRef.current.alertMqtt) toast.error("MQTT error", { description: reason });
    };

    client.on("connect",    onConnect);
    client.on("message",    onMessage);
    client.on("disconnect", onDisconnect);
    client.on("error",      onError);
    client.on("offline",    onDisconnect);

    // If already connected (singleton reuse)
    if (client.connected) onConnect();

    return () => {
      client.off("connect",    onConnect);
      client.off("message",    onMessage);
      client.off("disconnect", onDisconnect);
      client.off("error",      onError);
      client.off("offline",    onDisconnect);
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
    };
  }, [mqttRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTheme    = useCallback(() => setTheme((t) => t === "dark" ? "light" : "dark"), []);
  const updateSettings = useCallback((patch) => setSettings((s) => ({ ...s, ...patch })), []);
  const resetSettings  = useCallback(() => { setSettings({ ...DEFAULT_SETTINGS }); toast.success("Settings reset to defaults"); }, []);

  const reconnect = useCallback((host, user, pass) => {
    setMqttConnected(false);
    mqttReconnect(host, user, pass);
    setMqttRevision((r) => r + 1);
    toast.info("Reconnecting to MQTT…");
  }, []);

  const sendCommand = useCallback(async (device, action, label) => {
    try {
      publishCmd(device, action);
      toast.success(`Sent → ${label || `${device}: ${action}`}`, { description: "Published to MQTT" });
    } catch {
      toast.error("Command failed — MQTT offline");
    }
  }, []);

  const sendCamCommand = useCallback(async (cmd) => {
    try {
      publishCamCmd(cmd);
      toast.success(`Camera: ${cmd}`);
    } catch {
      toast.error("Camera command failed — MQTT offline");
    }
  }, []);

  const sendPzemCommand = useCallback(async (cmd, val, label) => {
    try {
      publishPzemCmd(cmd, val);
      toast.success(`PZEM → ${label || `${cmd}: ${val}`}`, { description: "Published to pzem_cmd" });
    } catch {
      toast.error("PZEM command failed — MQTT offline");
    }
  }, []);

  const addSnapshot   = useCallback((dataUrl) => {
    const item = { id: Date.now().toString(), url: dataUrl, ts: new Date().toISOString() };
    setSnapshots((s) => [item, ...s]);
    toast.success("Snapshot saved to gallery");
  }, []);
  const deleteSnapshot = useCallback((id) => setSnapshots((s) => s.filter((x) => x.id !== id)), []);
  const clearSnapshots = useCallback(() => setSnapshots([]), []);

  // Helper: history formatted for charts (last N points)
  const getHistory = useCallback((range = "1h") => {
    const minutes = range === "24h" ? 1440 : range === "7d" ? 10080 : 60;
    const since = Date.now() - minutes * 60 * 1000;
    return history.filter((p) => p._ts > since);
  }, [history]);

  const value = {
    theme, toggleTheme,
    telemetry, mqttConnected, lastUpdate, pzemOnline,
    history, getHistory,
    settings, updateSettings, resetSettings, reconnect,
    sendCommand, sendCamCommand, sendPzemCommand,
    snapshots, addSnapshot, deleteSnapshot, clearSnapshots,
    camImage, camState, lastMotion,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
